import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { metrics } from './client';

const Ctx = createContext(metrics);
export const useMetrics = () => useContext(Ctx);

export default function MetricsProvider({ children }) {
  useEffect(() => {
    const handler = e => {
      const el = e.target instanceof Element ? e.target : null;
      if (!el) return;
      metrics.add(metrics.clickEvent(el));
    };
    document.addEventListener('click', handler, { capture: true });
    return () => document.removeEventListener('click', handler, { capture: true });
  }, []);

  useEffect(() => {
    // Milestone-based scroll tracking: emit only when crossing 25/50/75/100%
    // thresholds. This captures the analytical signal (how far did the user
    // scroll?) while reducing event volume by ~99% vs per-frame emission.
    //
    // Previous time-based throttle attempts (2s in 8f06e63, 500ms in 7427347)
    // were reverted (466c2b1) because the time check inside the single RAF
    // callback was incompatible with the ticking flag pattern — short scrolls
    // were completely missed. Milestone-based emission avoids this: we check
    // on every RAF tick but only emit when a new threshold is crossed.
    const MILESTONES = [25, 50, 75, 100];
    const emittedMilestones = new Set();
    let currentPath = window.location.pathname;
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          // Reset milestones on SPA route change
          if (window.location.pathname !== currentPath) {
            emittedMilestones.clear();
            currentPath = window.location.pathname;
          }

          const scrollTop = window.scrollY;
          const docHeight = document.documentElement.scrollHeight - window.innerHeight;
          const pct = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;

          for (const milestone of MILESTONES) {
            if (pct >= milestone && !emittedMilestones.has(milestone)) {
              emittedMilestones.add(milestone);
              metrics.add(metrics.scrollEvent({ pct: milestone }));
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const onError = (event) => {
      metrics.add(metrics.errorEvent(event.error || { name: 'Error', message: event.message, stack: event?.error?.stack }));
    };
    const onRejection = (event) => {
      metrics.add(metrics.errorEvent(event.reason || { name: 'UnhandledRejection', message: String(event.reason) }));
    };
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
    };
  }, []);

  useEffect(() => {
    const iv = setInterval(() => metrics.flush(), 10000);
    return () => clearInterval(iv);
  }, []);

  const ioRef = useRef(null);
  useEffect(() => {
    ioRef.current = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const name = entry.target.getAttribute('data-metrics-name') || entry.target.id || entry.target.tagName.toLowerCase();
        metrics.add(metrics.visibilityEvent({ name, visible: entry.isIntersecting }));
      });
    }, { threshold: 0.1 });

    // Connect the observer to the MetricsClient singleton so components
    // can register elements via metrics.observe() / metrics.unobserve()
    // (used by the useVisibilityTracking hook).
    metrics.setObserver(ioRef.current);

    return () => {
      ioRef.current?.disconnect();
      metrics.setObserver(null);
    };
  }, []);

  // ---------------------------------------------------------------------------
  // Page Visibility Change tracking (Frontend Logging Gap Analysis — Proposal #3)
  // REMOVED: This global listener was emitting duplicate pageVisibilityChangeEvent
  // with was_recording: false (always inaccurate during recording).
  // SessionRightPanel.js handles recording-aware visibility tracking with correct
  // was_recording, session_id, and patient_id context.
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // Auth failure telemetry (Frontend Logging Gap Analysis — Proposal #2)
  // Listens for token:auth-failure custom events dispatched by index-core.js
  // when 401 retry is exhausted, and token:refresh-failed from TokenManager.
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const handleAuthFailure = (event) => {
      const detail = event.detail || {};
      metrics.add(metrics.authErrorEvent({
        error_type: 'token_auth_failure',
        silent_or_interactive: 'silent',
        error_message: detail.error || `401 on ${detail.url || 'unknown'}`,
      }));
      metrics.flush();
    };

    const handleRefreshFailed = (event) => {
      const detail = event.detail || {};
      metrics.add(metrics.authErrorEvent({
        error_type: 'token_refresh_failed',
        silent_or_interactive: 'silent',
        error_message: `Token refresh failed. Still valid: ${detail.cachedTokenStillValid}, expires in: ${detail.tokenExpiresIn}`,
      }));
      metrics.flush();
    };

    const handleReloginRequired = (event) => {
      const detail = event.detail || {};
      metrics.add(metrics.authErrorEvent({
        error_type: 'token_relogin_required',
        silent_or_interactive: 'interactive',
        error_message: `Relogin required: ${detail.reason || 'unknown'}. Consecutive expired: ${detail.consecutiveExpiredCount}`,
      }));
      metrics.flush();
    };

    window.addEventListener('token:auth-failure', handleAuthFailure);
    window.addEventListener('token:refresh-failed', handleRefreshFailed);
    window.addEventListener('token:relogin-required', handleReloginRequired);
    return () => {
      window.removeEventListener('token:auth-failure', handleAuthFailure);
      window.removeEventListener('token:refresh-failed', handleRefreshFailed);
      window.removeEventListener('token:relogin-required', handleReloginRequired);
    };
  }, []);

  const value = metrics;
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
